import type { getCurrentMembershipStatus } from "@/actions/membership";
import type { ImageData } from "@/db/schema/profile";
import type { MembershipState } from "@/features/hiives/components/hiive-detail-hub/hiive-page";
import { firstMediaPreviewUrl } from "@/lib/media";
import type { TierWithPrices } from "@/validations/tier";

type MembershipRecord = Awaited<ReturnType<typeof getCurrentMembershipStatus>>;
type ActiveTier = TierWithPrices;

export const HIIVE_DETAIL_LOAD_ERROR_MESSAGE =
  "We couldn't load this hiive right now. Please try again.";

// Avatars come from uploaded Hiive photos only — never an auth provider's
// image. See resolveProfileAvatarUrl in @/lib/media.
export const resolveAvatarUrl = (images: ImageData[] | null | undefined) =>
  firstMediaPreviewUrl(images);

export function resolveMembershipState(input: {
  membership: MembershipRecord;
  tiers: ActiveTier[];
  hasPendingJoinRequest: boolean;
}): MembershipState {
  const { membership, tiers, hasPendingJoinRequest } = input;

  if (membership?.status === "active") {
    const currentTier = tiers.find((tier) => tier.id === membership.tierId);
    const isFreeTier = currentTier?.prices.every(
      (price) => price.priceAmountMinor === 0,
    );

    if (isFreeTier) {
      const hasPaidPlans = tiers.some((tier) =>
        tier.prices.some((price) => price.priceAmountMinor > 0),
      );

      return hasPaidPlans ? "free_member_upgrade_available" : "free_member";
    }

    return membership.subscriptionStatus === "active"
      ? "paid_member"
      : "payment_pending";
  }

  if (membership?.status === "pending" || hasPendingJoinRequest) {
    return "pending_member";
  }

  return "not_member";
}
